import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactPaginate from "react-paginate";
import { postData } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";
import ViewInquiryModal from "../../../Modal/ViewInquiryModal";
import AssignmentHistoryModal from "../../../Modal/AssignmentHistoryModal";
import AgentSelectionModal from "../../../Modal/AgentSelectionModal";

const FollowupLeads = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInquiries, setTotalInquiries] = useState(0);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [currentInquiryHistory, setCurrentInquiryHistory] = useState([]);
  const [pageLimit, setPageLimit] = useState(10);
  const [cityFilterEnabled, setCityFilterEnabled] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showAgentModal, setShowAgentModal] = useState(false);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const response = await postData("api/lead-work-history/all", {
        limit: pageLimit,
        page: currentPage + 1,
        search: searchTerm.trim(),
        startDate,
        endDate,
        leadType: "homepage",
        status:
          statusFilter && statusFilter !== "ALL" ? statusFilter : undefined,
      });

      if (response?.success) {
        const data = response.data || {};
        const docs = data.docs || [];
        setInquiries(docs);
        setTotalInquiries(data.total || docs.length || 0);
        setTotalPages(data.pages || 1);
      } else {
        toast.error(response?.message || "Failed to fetch follow-up leads");
      }
    } catch (error) {
      toast.error("Error fetching follow-up leads");
      console.error("Fetch follow-up leads error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAssignedDate = (inquiry) => {
    const lead = inquiry.lead || {};
    const history = lead.assignHistory || [];

    if (Array.isArray(history) && history.length > 0) {
      const latest = history[history.length - 1];
      return formatDate(latest.assignedAt);
    }

    return formatDate(inquiry.createdAt);
  };

  useEffect(() => {
    fetchInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    searchTerm,
    startDate,
    endDate,
    statusFilter,
    pageLimit,
    cityFilterEnabled,
  ]);

  const toggleCityFilter = (e) => {
    if (!e.target.checked) {
      setSearchTerm("");
    }
    setCurrentPage(0);
    setCityFilterEnabled(e.target.checked);
  };

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchInquiries();
  };

  const handleReset = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("ALL");
    setCurrentPage(0);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "NEW":
        return "Assigned";
      case "ASSIGNED":
        return "Assigned";
      case "IN_PROGRESS":
        return "In Progress";
      case "FOLLOW_UP":
        return "Follow Up";
      case "CLOSED":
        return "Closed";
      case "NOT_INTERESTED":
        return "Not Interested";
      default:
        return status || "N/A";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleView = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowViewModal(true);
  };

  // Show full assignment history for a particular lead using lead.assignHistory
  const handleShowHistory = (lead) => {
    if (!lead) return;

    const historyForLead = Array.isArray(lead.assignHistory)
      ? lead.assignHistory
      : [];

    setCurrentInquiryHistory(historyForLead);
    setShowHistoryModal(true);
  };

  const handleAgentClick = (agent) => {
    if (!agent?._id) return;
    const userName = agent.fullName || "Agent";
    navigate(`/user-followup-leads/${agent._id}`, {
      state: { userName },
    });
  };

  const handleFollowupHistoryClick = (lead) => {
    if (!lead?._id) return;
    navigate(`/followup-leads/history`, {
      state: { lead },
    });
  };

  const toggleLeadSelection = (leadId) => {
    if (!leadId) return;
    setSelectedLeads((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      const allLeadIds = inquiries
        .map((inquiry) => inquiry.lead?._id)
        .filter((id) => !!id);
      setSelectedLeads(allLeadIds);
    } else {
      setSelectedLeads([]);
    }
  };

  const handleAgentSelect = async (agent) => {
    if (!agent?._id || selectedLeads.length === 0) {
      toast.error("No agent or leads selected");
      return;
    }

    try {
      setLoading(true);
      const response = await postData("api/enquiries/assign", {
        enquiryIds: selectedLeads,
        assignedTo: agent._id,
        assignedToModel: "User",
        note: `Assigned by admin at ${new Date().toLocaleString()}`,
      });

      if (response.success) {
        toast.success(
          `Successfully assigned ${selectedLeads.length} leads to ${agent.name}`
        );
        fetchInquiries();
        setSelectedLeads([]);
      } else {
        throw new Error(response.message || "Failed to assign leads");
      }
    } catch (error) {
      console.error("Assignment error:", error);
      toast.error(error.message || "Error assigning leads");
    } finally {
      setLoading(false);
      setShowAgentModal(false);
    }
  };

  return (
    <>
      <div className="content-page">
        <ToastContainer position="top-right" autoClose={3000} />
        <CustomLoader loading={loading} />
        <div className="content">
          <div className="container-fluid">
            <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
              <div className="flex-grow-1">
                <h4 className="fs-18 fw-semibold m-0">Followup Leads</h4>
                <p className="text-muted mb-0">
                  Total Assigned Leads: {totalInquiries}
                  {selectedLeads.length > 0 &&
                    ` | Selected: ${selectedLeads.length}`}
                </p>
              </div>
              <div className="mt-2 mt-sm-0">
                {selectedLeads.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowAgentModal(true)}
                    disabled={loading}
                  >
                    <i className="mdi mdi-account-switch-outline me-1"></i>
                    Assign to Agent
                  </button>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <form onSubmit={handleSubmit}>
                      <div className="row g-3 align-items-end">
                        <div className="col-md-12 col-lg-2">
                          <label htmlFor="pageLimit" className="form-label">
                            Items per page
                          </label>
                          <select
                            className="form-select"
                            id="pageLimit"
                            value={pageLimit}
                            onChange={(e) => {
                              setPageLimit(Number(e.target.value));
                              setCurrentPage(0);
                            }}
                          >
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                        </div>
                        <div className="col-md-12 col-lg-2">
                          <div className="form-check form-switch d-flex align-items-center h-100">
                            <input
                              className="form-check-input me-2"
                              type="checkbox"
                              id="cityFilterToggle"
                              checked={cityFilterEnabled}
                              onChange={toggleCityFilter}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="cityFilterToggle"
                            >
                              City Filter
                            </label>
                          </div>
                        </div>
                        <div className="col-md-12 col-lg-3">
                          <label htmlFor="Search" className="form-label">
                            Search
                          </label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control ps-4"
                              placeholder={
                                cityFilterEnabled
                                  ? "Search disabled when city filter is active"
                                  : "Search by name, email or mobile..."
                              }
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              readOnly={cityFilterEnabled}
                            />
                            <span className="input-group-text">
                              <i className="mdi mdi-magnify"></i>
                            </span>
                          </div>
                          {cityFilterEnabled && (
                            <small className="text-primary mt-1 d-block">
                              <i className="mdi mdi-information-outline me-1"></i>
                              Showing results for selected city only
                            </small>
                          )}
                        </div>

                        <div className="col-md-12 col-lg-2">
                          <label htmlFor="statusFilter" className="form-label">
                            Status
                          </label>
                          <select
                            id="statusFilter"
                            className="form-select"
                            value={statusFilter}
                            onChange={(e) => {
                              setStatusFilter(e.target.value);
                              setCurrentPage(0);
                            }}
                          >
                            <option value="ALL">All</option>
                            <option value="NEW">Assigned</option>
                            <option value="IN_PROGRESS">In Progress</option>
                            <option value="FOLLOW_UP">Follow Up</option>
                            <option value="CLOSED">Closed</option>
                            <option value="NOT_INTERESTED">
                              Not Interested
                            </option>
                          </select>
                        </div>

                        <div className="col-md-12 col-lg-2">
                          <label htmlFor="startDate" className="form-label">
                            From Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            id="startDate"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                        </div>

                        <div className="col-md-12 col-lg-2">
                          <label htmlFor="endDate" className="form-label">
                            To Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            id="endDate"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                          />
                        </div>

                        <div className="col-md-12 col-lg-3 d-flex gap-2 align-items-end">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            Search
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleReset}
                            disabled={loading}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mt-2">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    {loading ? (
                      <div className="text-center py-5">
                        <CustomLoader />
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover table-centered mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: "50px" }}>
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedLeads.length > 0 &&
                                    selectedLeads.length ===
                                      inquiries.filter((q) => q.lead?._id)
                                        .length
                                  }
                                  onChange={toggleSelectAll}
                                />
                              </th>
                              <th>S.No</th>
                              <th>Lead Name</th>
                              <th>Agent</th>
                              <th>Assign Count</th>
                              <th>Status</th>
                              <th>Note</th>
                              <th>Assigned Date</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inquiries?.length > 0 ? (
                              inquiries.map((inquiry, index) => {
                                const lead = inquiry.lead || {};
                                const agent = inquiry.user || {};
                                return (
                                  <tr key={inquiry._id}>
                                    <td>
                                      <input
                                        type="checkbox"
                                        checked={selectedLeads.includes(
                                          lead._id
                                        )}
                                        onChange={() =>
                                          toggleLeadSelection(lead._id)
                                        }
                                      />
                                    </td>
                                    <td>
                                      {currentPage * pageLimit + index + 1}
                                    </td>
                                    <td>{lead.fullName || "N/A"}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 text-primary text-decoration-underline"
                                        onClick={() => handleAgentClick(agent)}
                                      >
                                        {agent.fullName || "N/A"}
                                      </button>
                                    </td>
                                    <td>
                                      {Array.isArray(lead.assignHistory) &&
                                      lead.assignHistory.length > 0 ? (
                                        <button
                                          type="button"
                                          className="btn btn-link p-0 text-primary text-decoration-underline"
                                          onClick={() =>
                                            handleShowHistory(lead)
                                          }
                                        >
                                          {lead.assignHistory.length}
                                        </button>
                                      ) : (
                                        0
                                      )}
                                    </td>
                                    <td>{getStatusLabel(inquiry.status)}</td>
                                    <td>{inquiry.note || "-"}</td>
                                    <td>{getAssignedDate(inquiry)}</td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        <button
                                          className="btn btn-sm btn-primary"
                                          onClick={() => handleView(lead)}
                                        >
                                          <i className="mdi mdi-eye"></i> View
                                        </button>
                                        <button
                                          className="btn btn-sm btn-secondary"
                                          onClick={() =>
                                            handleFollowupHistoryClick(lead)
                                          }
                                        >
                                          Follow-up History
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center py-4">
                                  No follow-up leads found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {totalPages > 1 && (
                          <ReactPaginate
                            previousLabel={
                              <i className="mdi mdi-chevron-left"></i>
                            }
                            nextLabel={
                              <i className="mdi mdi-chevron-right"></i>
                            }
                            breakLabel="..."
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            pageCount={totalPages}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={handlePageChange}
                            containerClassName="pagination pagination-rounded justify-content-end mt-3"
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="page-item"
                            previousLinkClassName="page-link"
                            nextClassName="page-item"
                            nextLinkClassName="page-link"
                            activeClassName="active"
                            forcePage={currentPage}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ViewInquiryModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        inquiry={selectedInquiry}
      />

      <AssignmentHistoryModal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        history={currentInquiryHistory}
      />

      <AgentSelectionModal
        show={showAgentModal}
        onClose={() => setShowAgentModal(false)}
        onSelect={handleAgentSelect}
      />
    </>
  );
};

export default FollowupLeads;
